import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import {
  ChevronsUpDown,
  CompassIcon,
  CreditCardIcon,
  FlagIcon,
  HouseIcon,
  LogOutIcon,
  MessageCircle,
  MessageCircleQuestionMark,
  PanelLeftClose,
  PanelRightClose,
  Plug,
  Plus,
  Sparkles,
  Table,
  User,
} from "lucide-react";
import { useState, type Dispatch, type FC, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import Text from "@/components/Text";
import { Link, useLocation, useNavigate } from "react-router";
import { currentSubscriptionState } from "@/store/currentSubscriptionState";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { resetUser, userState } from "@/store/userState";
import { triggerEvent } from "@/lib/utils";
import { signOut } from "aws-amplify/auth";
import { userRestaurantsState } from "@/store/userRestaurantsState";
import { remainingKrunshCreditsState } from "@/store/remainingKrunshCreditsState";
import KrunshCoinDialog from "../dialogs/KrunshCoinDialog";

type Props = {
  setOpen: Dispatch<SetStateAction<boolean>>;
  open: boolean;
};

const AppSidebar: FC<Props> = ({ setOpen, open }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { data: stripeSubscription } = currentSubscriptionState();
  const { data: userData } = userState();
  const { data: restaurants } = userRestaurantsState();
  const { data: remainingKrunshCredits } = remainingKrunshCreditsState();

  const [openKrunshCoinDialog, setOpenKrunshCoinDialog] = useState(false);

  return (
    <>
      <KrunshCoinDialog
        open={openKrunshCoinDialog}
        onClose={() => setOpenKrunshCoinDialog(false)}
      />

      <Sidebar collapsible="icon" className="hidden sm:flex">
        <SidebarHeader
          style={{
            justifyContent: open ? "space-between" : "center",
          }}
          className="flex-row items-center w-[100%]"
        >
          {open && (
            <img
              alt="krunsh logo"
              src="/svg/krunsh-logo.svg"
              className="h-[36px]"
            />
          )}
          {open ? (
            <PanelLeftClose
              width={20}
              height={20}
              color="#758195"
              className="cursor-pointer"
              onClick={() => setOpen(false)}
            />
          ) : (
            <PanelRightClose
              width={20}
              height={20}
              color="#758195"
              className="cursor-pointer"
              onClick={() => setOpen(true)}
            />
          )}
        </SidebarHeader>

        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu>
              <SidebarMenuItem className="flex flex-col gap-[4px]">
                <SidebarMenuButton
                  isActive={pathname === "/"}
                  onClick={() => {
                    navigate("/");
                  }}
                  className="h-[38px] group-data-[collapsible=icon]:p-[6px]"
                >
                  <HouseIcon height={20} width={20} color="#758195" />
                  <Text fontWeight={600} fontSize={14} color="#44546F">
                    {t("home")}
                  </Text>
                </SidebarMenuButton>
                <SidebarMenuButton
                  isActive={pathname.includes("/explorer")}
                  onClick={() => {
                    navigate("/explorer");
                  }}
                  className="h-[38px] group-data-[collapsible=icon]:p-[6px]"
                >
                  <CompassIcon height={20} width={20} color="#758195" />
                  <Text fontWeight={600} fontSize={14} color="#44546F">
                    {t("explorer")}
                  </Text>
                </SidebarMenuButton>
                {/* <SidebarMenuButton
                  isActive={pathname === "/alerts"}
                  onClick={() => {
                    navigate("/alerts");
                  }}
                  className="h-[38px] group-data-[collapsible=icon]:p-[6px] relative"
                >
                  <Bell height={20} width={20} color="#758195" />
                  <Text fontWeight={600} fontSize={14} color="#44546F">
                    {t("alerts")}
                  </Text>
                  {open && unreadNotificationsCount > 0 && (
                    <div className="absolute right-[5px] px-[4px] min-w-[20px] h-[20px] flex items-center justify-center rounded-[8px] bg-[#0F62FE]">
                      <Text color="#FFFFFF" fontSize={12} fontWeight={600}>
                        {unreadNotificationsCount}
                      </Text>
                    </div>
                  )}
                </SidebarMenuButton> */}
                <SidebarMenuButton
                  isActive={pathname.includes("/grid-builder")}
                  onClick={() => {
                    navigate("/grid-builder");
                  }}
                  className="h-[38px] group-data-[collapsible=icon]:p-[6px]"
                >
                  <Table height={20} width={20} color="#758195" />
                  <Text fontWeight={600} fontSize={14} color="#44546F">
                    Builder
                  </Text>
                </SidebarMenuButton>
                <SidebarMenuButton
                  isActive={pathname === "/restaurants"}
                  onClick={() => {
                    navigate("/restaurants");
                  }}
                  className="h-[38px] group-data-[collapsible=icon]:p-[6px] relative"
                >
                  <FlagIcon height={20} width={20} color="#758195" />
                  <Text fontWeight={600} fontSize={14} color="#44546F">
                    {t("stores")}
                  </Text>
                  {open && restaurants && restaurants.length > 0 && (
                    <div className="absolute right-[5px] px-[4px] min-w-[20px] h-[20px] flex items-center justify-center rounded-[8px] bg-[#0F62FE]">
                      <Text color="#FFFFFF" fontSize={12} fontWeight={600}>
                        {restaurants.length}
                      </Text>
                    </div>
                  )}
                </SidebarMenuButton>
                <SidebarMenuButton
                  isActive={pathname === "/connectors"}
                  onClick={() => {
                    navigate("/connectors");
                  }}
                  className="h-[38px] group-data-[collapsible=icon]:p-[6px] relative"
                >
                  <Plug height={20} width={20} color="#758195" />
                  <Text fontWeight={600} fontSize={14} color="#44546F">
                    Connecteurs
                  </Text>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter>
          {open &&
          (!stripeSubscription || stripeSubscription.cancelAtPeriodEnd) ? (
            <Button
              className="mb-[12px]"
              onClick={() => {
                navigate("/upgrade");
              }}
            >
              {t("upgrade")}
            </Button>
          ) : null}

          <SidebarMenu>
            <SidebarMenuItem>
              <div
                onClick={() => setOpenKrunshCoinDialog(true)}
                style={{ border: "1px solid #E5E7EB" }}
                className="p-[12px] rounded-[12px] bg-[#F7F8F9] flex items-center justify-between cursor-pointer mb-[12px]"
              >
                <div className="flex items-center gap-[8px]">
                  <Sparkles height={24} width={24} color="#1D4ED8" />
                  <div className="flex flex-col">
                    <Text fontWeight={400} fontSize={12} color="#6B7280">
                      {remainingKrunshCredits !== null &&
                      remainingKrunshCredits > 1
                        ? "Crédits IA"
                        : "Crédit IA"}
                    </Text>
                    <Text fontWeight={600} fontSize={16} color="#1F2937">
                      {remainingKrunshCredits !== null
                        ? remainingKrunshCredits
                        : "0"}
                    </Text>
                  </div>
                </div>
                <Plus height={16} width={16} color="#1D4ED8" />
              </div>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="items-center py-[24px]">
                  <SidebarMenuButton className="w-full justify-between mb-[12px]">
                    {open && (
                      <div className="flex flex-col">
                        <Text fontWeight={600} fontSize={14} color="#44546F">
                          {userData?.name}
                        </Text>
                        <Text fontSize={12} color="#758195">
                          {userData?.email}
                        </Text>
                      </div>
                    )}

                    <ChevronsUpDown />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="p-[16px] bg-[#FCFCFD] rounded-[12px] mb-[-50px]"
                  align="start"
                  style={{
                    marginLeft: open ? 250 : 45,
                  }}
                >
                  <DropdownMenuGroup>
                    <Link to="/profile">
                      <DropdownMenuItem>
                        <User />
                        <Text fontSize={14} fontWeight={600}>
                          {t("profile")}
                        </Text>
                      </DropdownMenuItem>
                    </Link>
                    <Link to="/subscriptions">
                      <DropdownMenuItem>
                        <CreditCardIcon />
                        <Text fontSize={14} fontWeight={600}>
                          {t("subscription_and_payments")}
                        </Text>
                      </DropdownMenuItem>
                    </Link>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`https://krunsh.crisp.help/${navigator.language.split("-")[0] === "fr" ? "fr" : "en"}/`}
                    >
                      <DropdownMenuItem>
                        <MessageCircleQuestionMark />
                        <Text fontSize={14} fontWeight={600}>
                          {t("helper_center")}
                        </Text>
                      </DropdownMenuItem>
                    </a>

                    <DropdownMenuItem
                      onClick={() => {
                        // @ts-ignore
                        $crisp.push(["do", "chat:open"]);
                        // @ts-ignore
                        $crisp.push(["do", "chat:show"]);
                      }}
                    >
                      <MessageCircle />
                      <Text fontSize={14} fontWeight={600}>
                        {t("chat_with_us")}
                      </Text>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={async () => {
                        triggerEvent("User Logged Out");
                        await signOut();
                        resetUser();
                        navigate("/explorer/sign-in");
                      }}
                    >
                      <LogOutIcon color="#AE2E24" />
                      <Text fontSize={14} fontWeight={600} color="#AE2E24">
                        {t("log_out")}
                      </Text>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
      </Sidebar>
    </>
  );
};

export default AppSidebar;
